import { useEffect, useState } from 'react';
import Video, { Room, LocalDataTrack } from 'twilio-video';
import ServiceApi from 'vl-common/src/services/service.api';
import { CallManager } from '@helpers/CallManager';
import { LocalTrackPublication } from 'twilio-video/tsdef/LocalTrackPublication';
import { Appointment } from 'vl-common/src/schemas/shared';
import { Lobby } from '@lib/Lobby';
import { Modal } from 'antd';
import { HTTPError } from 'ky';
import assert from 'assert';
import { Mutex } from 'async-mutex';

// Twilio room names are just special strings
type TwilioRoomName = string & {}; //NOSONAR

// use*Room either returns a Room and a fully operational DataTrack along with its name and a signallingManager OR
// a failure flag and an optional error
type UseRoomResult =
  | {
      isReady: true;
      room: Room;
      roomName: TwilioRoomName;
      dataTrack: LocalDataTrack;
      signalingManager: CallManager;
    }
  | {
      isReady: false;
      error?: Error;
    };

// Rooms are created based on this spec
type RoomSpec =
  | {
      // test rooms are (currently) keyed by the participant wanting to execute the test.
      user_guid: string;
    }
  | {
      // appointment rooms are primarily keyed by the appointment id (a.k.a. the identity) ...
      appointment_id: string | number;
      // ... however, because the clinician can reschedule appointments, I also include the appointment start time
      // in the key to ensure that promiseToDoIt doesn't end up waiting for a token that is waiting on a stale far
      // distant timeout started when the appointment was originally scheduled far into the future. By including the
      // startTime in the key, I ensure that each *version* of the appointment gets its own timeout waiting for
      // videotokengen to eventually return a token for the actual appointment start time. For appointments that are
      // pushed later there is no problem because videotokengen will simply instruct the timeout loop to wait again. The
      // problem is with appointments that are brought forward. If the revised appointment creates a room and starts a
      // call but while that call is still in progress the original appointment timeout trys to create a second instance
      // of the room it is in danger of screwing up the call. To safeguard against this promiseToDoIt ensures that
      // rooms are unique by room name.
      start: Date;
    };

// RoomSpecJSON is just a special string
type RoomSpecJSON = string & {}; //NOSONAR

// a cache of Rooms wrapped up in UseRoomResult's and indexed by RoomSpecJSON
const roomPromiseCache = new Map<RoomSpecJSON, Promise<UseRoomResult>>();

// a cache of Rooms indexed by TwilioRoomName
const roomCache = new Map<TwilioRoomName, Room>();

// a Mutex to stop multiple simultaneous calls to Video.connect()
const videoConnectMutex = new Mutex();

/**
 * Ensures a specific LocalTrack is present on the LocalParticipant within 5 seconds
 * Returns the LocalTrackPublication.
 *
 * This compensates for Twilio's surprising behaviour ... Video.connect() can be called with a local
 * data track, BUT IT MAY NOT BE PROPERLY ATTACHED by the time Video.connect() resolves. Twilio will
 * not add the data track to room.localParticipant.dataTracks until the track *is* published. Without waiting
 * for the track publication, there is a danger that we try to send a data track message without the data track
 * in place. Disaster.
 */
function waitForDataTrackPublication(room: Room, localTrack: LocalDataTrack) {
  const participant = room.localParticipant;

  return new Promise((resolve, reject) => {
    // 1. Check if it's already in the collection (Iterate values to avoid ID/SID mismatch)
    const existingPub = Array.from(participant.dataTracks.values()).find((pub) => pub.track === localTrack);

    if (existingPub) {
      return resolve(existingPub);
    }

    // 2. If not, wait for the event
    const onPublished = (publication: LocalTrackPublication) => {
      if (publication.track === localTrack) {
        cleanup();
        console.log('Track found:', publication, room);
        resolve(publication);
      }
    };

    const onPublishedFailed = (error: Error) => reject(error);

    const cleanup = () => {
      participant.off('trackPublished', onPublished);
      participant.off('trackPublicationFailed', onPublishedFailed);
    };

    participant.on('trackPublished', onPublished);
    participant.on('trackPublicationFailed', onPublishedFailed);

    // Optional: Add a timeout to prevent hanging forever
    setTimeout(() => {
      cleanup();
      reject(Error('Timed out waiting for track to publish'));
    }, 5_000);
  });
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function waitForRoomToken(spec: RoomSpec) {
  while (true) {
    const { data, status, statusText } = await ServiceApi.get('/videotokengen', {
      params: spec
    }).catch((e) => {
      if (e instanceof HTTPError) return { status: e.response.status, statusText: e.message, response: e.response };

      return { status: e.status || e.response?.status || 500, statusText: e.message };
    });

    // seems to suffer from cold starts returning 502 Bad Gateways. Give it another 3 seconds to wake
    // up and try again
    if (status === 502) {
      await delay(3_000);
      continue;
    }

    if (status > 299 || !data) {
      throw Error(`Error fetching video token: ${statusText} (${status})`);
    }

    const { retryAfterMs, token, roomName } = data;

    if (!retryAfterMs) {
      return { token, roomName, isReady: true };
    }

    if (retryAfterMs === 'Infinity') {
      return { isReady: false };
    }

    // wait for the timeout. Might wait too long if the appointment was rescheduled, but shouldn't
    // matter because an earlier appointment/startDate pair should already have resolved
    await delay(retryAfterMs);

    // loop back and try again
  }
}

// returns a Room with the given name after the data track has be published
async function findOrCreateRoomByName(
  roomName: TwilioRoomName,
  token: string
): Promise<{ room: Room; dataTrack: LocalDataTrack }> {
  // Because clinicians can reschedule appointments, and I now include the start time
  // of the appointment in the RoomSpec I can have multiple room specs each with the same room name (but different start
  // times). So I need to ensure that I *don't* create multiple Twilio Rooms. Hence, check the roomCache for previously
  // created rooms. And to be clear the tokengenerator lambda will ensure that regardless of when the requestor *thinks*
  // the appointment should start it'll be made to wait until it actually *should* start, even in light of reschedules.
  if (roomCache.has(roomName)) {
    const room = roomCache.get(roomName)!;
    const dataTrack = room.localParticipant.dataTracks.values().next().value?.track;

    console.log('>>> returning previously cached room', { room, dataTrack });
    assert(room.state !== 'disconnected', 'Room should not be disconnected');
    assert(dataTrack, 'Room should have a dataTrack');

    // just in case ...
    await waitForDataTrackPublication(room, dataTrack);

    return {
      room,
      dataTrack
    };
  }

  // it is just possible that multiple functions race to this point. And since Room creation is asynchronous, I need to
  // serialise execution here to avoid any possibility of multiple rooms being created.
  return await videoConnectMutex.runExclusive(async () => {
    // either I'm the first one into this block and this test will fail or someone has already created the
    // room, in which case just return it
    if (roomCache.has(roomName)) {
      const room = roomCache.get(roomName)!;

      assert(room.localParticipant.dataTracks.size >= 1, 'Room expected to have one or more data tracks');
      return { room, dataTrack: room.localParticipant.dataTracks.values().next().value!.track };
    }

    const dataTrack = new LocalDataTrack();
    const room = await Video.connect(token, {
      tracks: [dataTrack],
      name: roomName,
      rtcConfiguration: { iceTransportPolicy: 'relay' }
    });
    roomCache.set(roomName, room);

    room.localParticipant.on('trackPublicationFailed', () => {
      Modal.error({
        title: 'Video Connection Error',
        content:
          'Failed to connect to the video room. Please refresh the page and try again. If the problem persists, please contact support.'
      });
    });

    room.on('participantConnected', (participant) => {
      participant.on('trackSubscriptionFailed', () => {
        Modal.error({
          title: 'Media Connection Error',
          content:
            'Failed to connect to remote audio and/or video. Please refresh the page and try again. If the problem persists, please contact support.'
        });
      });
    });

    await waitForDataTrackPublication(room, dataTrack);

    return { room, dataTrack };
  });
}

async function promiseToCreateRoom(spec: RoomSpec): Promise<UseRoomResult> {
  const { token, roomName, isReady } = await waitForRoomToken(spec);

  if (!isReady) {
    return { isReady: false };
  }

  if (!token || !roomName) {
    throw new Error('Missing video token or room name.');
  }

  const { room, dataTrack } = await findOrCreateRoomByName(roomName, token);

  return {
    room,
    roomName,
    dataTrack,
    signalingManager: new CallManager(room, dataTrack, {}),
    isReady: true
  };
}

export async function clearPreviouslyPromisedRooms() {
  const roomResults = await Promise.all([...roomPromiseCache.values()].map((p) => p.catch(() => undefined)));
  const connectedRooms = roomResults
    .filter((rr) => rr !== undefined)
    .filter((rr) => rr.isReady)
    .map((rr) => rr.room)
    .filter((r) => r.state !== 'disconnected');

  await Promise.all(connectedRooms.map((room) => Lobby.forRoom(room).leave()));
  roomPromiseCache.clear();
  roomCache
    .values()
    .filter((r) => r.state !== 'disconnected')
    .forEach((r) => r.disconnect());
  roomCache.clear();
}

// make roomPromiseCache accessible from the console for debugging purposes.
if (typeof window !== 'undefined') {
  // @ts-ignore
  window.promisedPreviously = roomPromiseCache;
}

function createOrFindRoomPromise(spec: RoomSpec): Promise<UseRoomResult> {
  const roomKey = JSON.stringify(spec);

  if (!roomPromiseCache.has(roomKey)) {
    roomPromiseCache.set(
      roomKey,
      promiseToCreateRoom(spec).catch((e) => {
        Modal.error({
          title: 'Video Connection Error',
          content: `Failed to connect to the video room. Please refresh the page and try again. If the problem persists, please contact support: ${e.message}`
        });
        return { isReady: false, error: e };
      })
    );
  }

  return roomPromiseCache.get(roomKey)!;
}

export function useTestPatientRoom(patientOrClinicianGuid: string) {
  const [result, setResult] = useState<UseRoomResult | undefined>(undefined);

  useEffect(() => {
    createOrFindRoomPromise({ user_guid: patientOrClinicianGuid }).then((r) => {
      setResult(r);
    });
  }, [patientOrClinicianGuid]);

  return {
    room: undefined,
    roomName: undefined,
    isReady: false,
    dataTrack: undefined,
    signalingManager: undefined,
    error: undefined,
    ...result
  };
}

// (Eventually) returns a cached Room associated with the given appointment all suitably wired up for
// DataTrack signalling.
export function useAppointmentRoomDT(appointment: Appointment) {
  const { appointment_id, start, end, case_review } = appointment;
  const [result, setResult] = useState<UseRoomResult | undefined>(undefined);

  useEffect(() => {
    const isAppointmentEnded = new Date(end) < new Date();

    if (isAppointmentEnded || case_review) return;

    createOrFindRoomPromise({ appointment_id, start }).then((r) => {
      setResult(r);
    });
  }, [appointment_id]);

  return {
    room: undefined,
    roomName: undefined,
    isReady: false,
    dataTrack: undefined,
    signalingManager: undefined,
    error: undefined,
    ...result
  };
}
